import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Search, User as UserIcon, LogOut, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePublicAppSettings } from "@/lib/queries";
import { fmtDate } from "@emr/shared/india";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { EditProfileDialog } from "@/components/EditProfileDialog";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

export function Topbar({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const { profile, isAdmin, signOut } = useAuth();
  const { data: settings } = usePublicAppSettings();
  const navigate = useNavigate();
  const [today, setToday] = useState(fmtDate(new Date()));
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState<{ id: string; name: string; clinicUhid: string; phone: string | null }[]>([]);

  useEffect(() => {
    const i = setInterval(() => setToday(fmtDate(new Date())), 60_000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (searchQ.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await apiFetch<{ rows: { id: string; name: string; clinicUhid: string; phone: string | null }[] }>(`/patients?q=${encodeURIComponent(searchQ)}&limit=8`);
        setResults(r.rows);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const initials = (profile?.fullName || "U").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-cream-200 bg-cream-50 px-4 no-print dark:bg-dark-800 dark:border-dark-700">
      <Button variant="ghost" size="icon" onClick={onToggleSidebar} className="text-muted hover:text-ink">
        <Menu size={20} />
      </Button>
      <div className="hidden items-center gap-2 md:flex">
        <span className="font-serif text-lg font-semibold text-maroon-700 dark:text-amber-500">{settings?.clinicName ?? "PS Neuro Centre"}</span>
      </div>
      <div className="ml-2 hidden text-sm text-muted lg:block">{today}</div>

      <div className="mx-auto flex w-full max-w-md items-center">
        <Popover open={searchOpen} onOpenChange={setSearchOpen}>
          <PopoverAnchor asChild>
            <div className="relative w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <Input
                placeholder="Search patient by UHID, phone, or name…"
                className="pl-9"
                value={searchQ}
                onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              />
            </div>
          </PopoverAnchor>
          <PopoverContent className="p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
            <Command shouldFilter={false}>
              <CommandList>
                {results.length === 0 && searchQ.length >= 2 && <CommandEmpty>No patients found.</CommandEmpty>}
                {results.length === 0 && searchQ.length < 2 && <CommandEmpty>Type at least 2 characters.</CommandEmpty>}
                <CommandGroup>
                  {results.map((r) => (
                    <CommandItem
                      key={r.id}
                      onSelect={() => { navigate(`/patients/${r.id}`); setSearchOpen(false); setSearchQ(""); }}
                      className="flex flex-col items-start"
                    >
                      <span className="font-medium">{r.name}</span>
                      <span className="text-xs text-muted">{r.clinicUhid} · {r.phone ?? "no phone"}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 px-2 hover:bg-cream-100 dark:hover:bg-dark-700">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-maroon-600 text-xs font-semibold text-cream-50">{initials}</div>
            <span className="hidden text-sm font-medium text-ink dark:text-cream-50 md:inline">{profile?.fullName ?? "User"}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuLabel className="text-xs text-muted">{isAdmin ? "Administrator" : "User"}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <EditProfileDialog>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}><UserIcon size={14} className="mr-2" /> Edit profile</DropdownMenuItem>
          </EditProfileDialog>
          <ChangePasswordDialog>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}><KeyRound size={14} className="mr-2" /> Change password</DropdownMenuItem>
          </ChangePasswordDialog>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => signOut()} className="text-red-600 focus:text-red-600"><LogOut size={14} className="mr-2" /> Sign out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
